import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { VisibilityState } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Lock,
  Settings,
} from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import { Text } from '../../components/ui/text';
import { useIsMobile, useMediaQuery } from '../../hooks/use-media-query';
import { DND_DRAGGABLE_PROPS, DND_OVERLAY_PROPS } from '../../lib/dnd-attrs';
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorFooter,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  useEntitySelectorSearch,
  type EntityItemConfig,
  type EntitySelection,
  type EntitySelectorConfig,
} from '../entity-selector';

import type { DataTableColumnDef } from './data-table-types';
import {
  getColumnDefId,
  getColumnLabel,
  mergeColumnOrder,
} from './data-table-utils';
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// DataTableColumnSettings — "edit columns" panel
// ---------------------------------------------------------------------------
//
// An entity selector, because the column roster is unbounded: a workspace can
// carry hundreds of custom-field columns, and a flat switch list stops being
// navigable long before that. The panel splits into two regions:
//
//   Shown        the pinned + visible columns, in table order — the small,
//                curated set. Reorder lives here: dnd-kit drag on pointer
//                surfaces, up/down buttons on mobile viewports AND any touch
//                device (an iPad in landscape gets buttons, not a drag list).
//                The whole row is the drag surface (8px activation distance
//                keeps the Switch tappable), a grip reveals on hover, the
//                source row dims to 30%, and an elevated chip copy follows
//                the cursor via DragOverlay.
//   All columns  every column as a checkbox row, filtered by the search
//                input — the haystack. Checking shows a column, unchecking
//                hides it.
//
// Searching hides the Shown section: the query is about finding a column in
// the haystack, and the reorder list would push the matches off-screen.

const ROW_HEIGHT = 36;

// The Shown section is the curated set, but "curated" can still mean dozens —
// cap it so the pool below always stays reachable without scrolling the popup.
const SHOWN_MAX_HEIGHT = 216;

const POOL_ID = 'columns';

// Touch detection — no base equivalent; module-local.
function useIsTouchDevice(): boolean {
  return useMediaQuery('(hover: none), (pointer: coarse)');
}

export type DataTableColumnSettingsProps<TData> = Omittable<{
  /** The same column definitions passed to the DataTable. */
  columns: DataTableColumnDef<TData>[];
  /** Pinned ids render locked at the top — not reorderable. */
  pinnedColumnIds?: string[];
  columnOrder: string[];
  columnVisibility: VisibilityState;
  onColumnOrderChange: (order: string[]) => void;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  /** When provided, renders a "Reset columns" action that clears all customization. */
  onReset?: () => void;
  /** Accessible name for the gear trigger and the panel heading. */
  label?: string;
}>;

type ColumnEntry = Omittable<{
  id: string;
  label: string;
  canHide: boolean;
}>;

function LeadingGutter({ children }: { children?: React.ReactNode }) {
  return (
    <Box width={16} align="center" justify="center" flex="none">
      {children}
    </Box>
  );
}

function ColumnRow({
  entry,
  leading,
  visible,
  toggleDisabled,
  onToggleVisibility,
  hoverable,
  asListItem,
}: Omittable<{
  entry: ColumnEntry;
  leading: React.ReactNode;
  visible: boolean;
  /** Disabled beyond `canHide` — e.g. this is the last visible column. */
  toggleDisabled?: boolean;
  onToggleVisibility: (id: string, visible: boolean) => void;
  hoverable?: boolean;
  /**
   * Publish the row as a named item of the panel's list. Off for the copy
   * inside `DragOverlay`, which floats a duplicate of a row already in the
   * list, and off inside `SortableColumnRow`, whose drag wrapper is the item.
   */
  asListItem?: boolean;
}>) {
  return (
    <Box
      {...(asListItem
        ? { role: 'listitem', 'aria-label': entry.label }
        : null)}
      direction="row"
      align="center"
      gap="sm"
      rounded="md"
      paddingX="sm"
      height={ROW_HEIGHT}
      hoverBackground={hoverable}
    >
      {leading}
      <Box fill="flex" shrink>
        <Text size="sm" truncate>
          {entry.label}
        </Text>
      </Box>
      {/* Stop pointer-down so toggling never arms a row drag. */}
      <Box onPointerDown={(e) => e.stopPropagation()}>
        <Switch
          checked={visible}
          disabled={!entry.canHide || toggleDisabled}
          onCheckedChange={(value) => onToggleVisibility(entry.id, !!value)}
        />
      </Box>
    </Box>
  );
}

function SortableColumnRow({
  entry,
  visible,
  toggleDisabled,
  onToggleVisibility,
}: Omittable<{
  entry: ColumnEntry;
  visible: boolean;
  toggleDisabled?: boolean;
  onToggleVisibility: (id: string, visible: boolean) => void;
}>) {
  const [hovered, setHovered] = React.useState(false);
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: entry.id });

  const nodeRef = React.useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );

  return (
    <Box touchAction="none" userSelect="none"
      ref={nodeRef}
      data-testid={`data-table-column-settings-row-${entry.id}`}
      cursor={isDragging ? 'grabbing' : 'grab'}
      style={{
        // Pin x to 0 — the list only reorders vertically; horizontal jitter
        // reads as breakage in a narrow panel.
        transform:
          CSS.Transform.toString(
            transform ? { ...transform, x: 0 } : null,
          ) ?? undefined,
        transition: isSorting ? transition : undefined,
        opacity: isDragging ? 0.3 : 1,
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      {...(attributes as object)}
      {...(listeners as object)}
      {...DND_DRAGGABLE_PROPS}
      // The drag wrapper IS the row, so it is what the list names. dnd-kit's
      // own `role` is stripped above: it publishes `button`, and the row
      // holds a Switch, so a button here would nest two controls.
      role="listitem"
      aria-label={entry.label}
    >
      <ColumnRow
        entry={entry}
        visible={visible}
        toggleDisabled={toggleDisabled}
        onToggleVisibility={onToggleVisibility}
        hoverable
        leading={
          <LeadingGutter>
            {hovered || isDragging ? (
              <Icon as={GripVertical} size="sm" color="muted" />
            ) : null}
          </LeadingGutter>
        }
      />
    </Box>
  );
}

/**
 * The pinned + visible columns, in table order, with the reorder affordances.
 * Rendered inside the selector so it can yield to an active search: a typed
 * query is about finding a column in the pool below, and this list would push
 * the matches off-screen.
 */
function ShownColumns({
  label,
  pinnedEntries,
  shownEntries,
  columnVisibility,
  isToggleDisabled,
  onToggleVisibility,
  onMove,
  useReorderButtons,
}: Omittable<{
  label: string;
  pinnedEntries: ColumnEntry[];
  shownEntries: ColumnEntry[];
  columnVisibility: VisibilityState;
  isToggleDisabled: (entry: ColumnEntry) => boolean;
  onToggleVisibility: (id: string, visible: boolean) => void;
  /** Move `shownEntries[fromIndex]` to `toIndex` within the shown list. */
  onMove: (fromIndex: number, toIndex: number) => void;
  useReorderButtons: boolean;
}>) {
  const search = useEntitySelectorSearch();
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const shownIds = React.useMemo(
    () => shownEntries.map((e) => e.id),
    [shownEntries],
  );

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  // `useSensors` feeds its arguments into a useMemo deps array, so each call
  // must receive a constant number of sensors — `useReorderButtons` flips when
  // touch detection settles, so spreading a conditional list into one call
  // changes the deps size between renders. Build both fixed-shape lists and
  // pick between them instead.
  const pointerSensors = useSensors(pointerSensor);
  const noSensors = useSensors();
  const sensors = useReorderButtons ? noSensors : pointerSensors;

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const from = shownIds.indexOf(String(active.id));
      const to = shownIds.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      onMove(from, to);
    },
    [shownIds, onMove],
  );

  const handleDragCancel = React.useCallback(() => setActiveId(null), []);

  const activeEntry = React.useMemo(
    () =>
      activeId !== null
        ? (shownEntries.find((e) => e.id === activeId) ?? null)
        : null,
    [activeId, shownEntries],
  );

  if (search.trim()) return null;

  const renderMobileRow = (entry: ColumnEntry, index: number) => (
    <ColumnRow
      key={entry.id}
      asListItem
      entry={entry}
      visible={columnVisibility[entry.id] !== false}
      toggleDisabled={isToggleDisabled(entry)}
      onToggleVisibility={onToggleVisibility}
      leading={
        <Box direction="row">
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            aria-label={`Move ${entry.label} up`}
          >
            <Icon as={ChevronUp} size="sm" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={index === shownEntries.length - 1}
            onClick={() => onMove(index, index + 1)}
            aria-label={`Move ${entry.label} down`}
          >
            <Icon as={ChevronDown} size="sm" />
          </Button>
        </Box>
      }
    />
  );

  return (
    <Box direction="column" border="bottom" paddingY="2xs" shrink={false}>
      <Box paddingX="sm" className="py-1.5">
        <Text size="xs" weight="medium" variant="muted">
          Shown
        </Text>
      </Box>
      {/* One named list across both blocks, so every row — locked,
          drag-reorderable, or button-reorderable — is an item of the same
          list and carries its column name. */}
      <ScrollArea style={{ maxHeight: SHOWN_MAX_HEIGHT }}>
        <Box role="list" aria-label={label} paddingX="2xs">
          {pinnedEntries.map((entry) => (
            <ColumnRow
              key={entry.id}
              asListItem
              entry={entry}
              visible={columnVisibility[entry.id] !== false}
              toggleDisabled={isToggleDisabled(entry)}
              onToggleVisibility={onToggleVisibility}
              leading={
                <LeadingGutter>
                  <Icon as={Lock} size="sm" color="muted" />
                </LeadingGutter>
              }
            />
          ))}
          {useReorderButtons ? (
            shownEntries.map(renderMobileRow)
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={shownIds}
                strategy={verticalListSortingStrategy}
              >
                {shownEntries.map((entry) => (
                  <SortableColumnRow
                    key={entry.id}
                    entry={entry}
                    visible={columnVisibility[entry.id] !== false}
                    toggleDisabled={isToggleDisabled(entry)}
                    onToggleVisibility={onToggleVisibility}
                  />
                ))}
              </SortableContext>
              {/* DragOverlay does NOT portal itself — it positions with
                  `fixed`, and the popover popup is transform-positioned, which
                  makes a fixed descendant position relative to the popup
                  instead of the viewport. Portal it to body. The overlay
                  marker attributes go on the CHILD — DragOverlay drops
                  unknown props (see ../../lib/dnd-attrs.ts). */}
              {createPortal(
                <DragOverlay dropAnimation={null}>
                  {activeEntry ? (
                    <Box
                      surface="popover"
                      rounded="md"
                      border
                      {...DND_OVERLAY_PROPS}
                    >
                      <ColumnRow
                        entry={activeEntry}
                        visible={columnVisibility[activeEntry.id] !== false}
                        onToggleVisibility={onToggleVisibility}
                        leading={
                          <LeadingGutter>
                            <Icon as={GripVertical} size="sm" color="muted" />
                          </LeadingGutter>
                        }
                      />
                    </Box>
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          )}
        </Box>
      </ScrollArea>
    </Box>
  );
}

export function DataTableColumnSettings<TData>({
  columns,
  pinnedColumnIds,
  columnOrder,
  columnVisibility,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onReset,
  label = 'Columns',
}: DataTableColumnSettingsProps<TData>) {
  const isMobile = useIsMobile();
  const isTouch = useIsTouchDevice();
  // Up/down buttons replace the drag list on mobile viewports and on ANY
  // touch device — a wide touch tablet gets working buttons, not a
  // pointer-shaped drag list.
  const useReorderButtons = isMobile || isTouch;

  const { entries, pinnedEntries, orderableIds, fullOrder, visibleCount } =
    React.useMemo(() => {
      const byId = new Map<string, DataTableColumnDef<TData>>();
      const defaultIds: string[] = [];
      for (const def of columns) {
        const id = getColumnDefId(def);
        if (id === undefined) continue;
        byId.set(id, def);
        defaultIds.push(id);
      }
      // Tolerate stale/partial orders the same way the table does. The merge
      // PRESERVES ids that aren't currently rendered (gated columns keep
      // their saved slots) — filter those out for display, but keep them in
      // fullOrder so commits round-trip them.
      const order = mergeColumnOrder(columnOrder, defaultIds);
      const known = order.filter((id) => byId.has(id));
      const pinnedSet = new Set(pinnedColumnIds ?? []);
      // The table drops ALL column pinning on mobile (see DataTable's
      // effectivePinnedColumnIds) — mirror that so the panel never locks a
      // row against reordering on a viewport where the grid treats the
      // column as ordinary.
      const lockedSet = isMobile ? new Set<string>() : pinnedSet;
      const toEntry = (id: string): ColumnEntry => {
        const def = byId.get(id)!;
        return {
          id,
          label: getColumnLabel(def, id),
          // Pinned columns are never hideable — on ANY viewport, so a phone
          // can't persist a hidden state that strands a locked column on
          // desktop. The Lock affordance and the switch must agree.
          canHide: def.enableHiding !== false && !pinnedSet.has(id),
        };
      };
      let visibleCount = 0;
      for (const id of known) {
        if (columnVisibility[id] !== false) visibleCount += 1;
      }
      return {
        entries: known.map(toEntry),
        pinnedEntries: known.filter((id) => lockedSet.has(id)).map(toEntry),
        orderableIds: known.filter((id) => !lockedSet.has(id)),
        fullOrder: order,
        visibleCount,
      };
    }, [columns, columnOrder, pinnedColumnIds, isMobile, columnVisibility]);

  const isVisible = React.useCallback(
    (id: string) => columnVisibility[id] !== false,
    [columnVisibility],
  );

  // The reorderable rows of the Shown section: visible, not locked.
  const shownEntries = React.useMemo(() => {
    const pinnedIds = new Set(pinnedEntries.map((e) => e.id));
    return entries.filter((e) => !pinnedIds.has(e.id) && isVisible(e.id));
  }, [entries, pinnedEntries, isVisible]);

  const toggleVisibility = React.useCallback(
    (id: string, visible: boolean) => {
      // Never allow hiding the last visible column — a zero-column table
      // renders data rows with no cells and persists that way.
      if (!visible && visibleCount <= 1) return;
      onColumnVisibilityChange({ ...columnVisibility, [id]: visible });
    },
    [columnVisibility, onColumnVisibilityChange, visibleCount],
  );

  // Disable the switch (not just ignore the toggle) when this row is the
  // last visible column, so the constraint is visible.
  const isToggleDisabled = React.useCallback(
    (entry: ColumnEntry) => isVisible(entry.id) && visibleCount <= 1,
    [isVisible, visibleCount],
  );

  // Move a shown entry to a new position among the shown entries, then
  // rebuild the full order: the visible rows take the new sequence while
  // hidden and pinned ids keep their stored slots.
  const moveShown = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      const ids = shownEntries.map((e) => e.id);
      // Both callers index into the shown list itself — the up/down buttons
      // are disabled at the ends, and a drag resolves both ends through
      // `indexOf` on this list — so the splice always removes one entry.
      const moved = ids.splice(fromIndex, 1)[0]!;
      ids.splice(toIndex, 0, moved);
      const shownSet = new Set(ids);
      let cursor = 0;
      // `shownSet` was built from `ids`, so the number of hits over
      // `fullOrder` is exactly `ids.length` — the cursor cannot outrun it.
      const next = fullOrder.map((id) =>
        shownSet.has(id) ? ids[cursor++]! : id,
      );
      onColumnOrderChange(next);
    },
    [shownEntries, fullOrder, onColumnOrderChange],
  );

  // ---- entity-selector plumbing -------------------------------------------

  const items = React.useMemo<EntityItemConfig[]>(
    () =>
      entries.map((entry) => ({
        id: entry.id,
        title: entry.label,
        // A row that cannot change state is disabled where it stands: an
        // unhideable column, or the last visible one.
        disabled:
          (!entry.canHide && isVisible(entry.id)) ||
          (isVisible(entry.id) && visibleCount <= 1),
        tooltip: !entry.canHide ? 'This column is always shown' : undefined,
      })),
    [entries, isVisible, visibleCount],
  );

  const itemsById = React.useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );

  const config = React.useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        {
          id: POOL_ID,
          label: 'All columns',
          items,
          hideAvatars: true,
        },
      ],
      searchPlaceholder: 'Search columns...',
      emptyMessage: 'No columns found',
    }),
    [items],
  );

  // Selection state IS visibility: a column is selected iff it is visible.
  const selections = React.useMemo<EntitySelection[]>(
    () =>
      entries
        .filter((entry) => isVisible(entry.id))
        .map((entry) => ({
          id: entry.id,
          poolId: POOL_ID,
          entity: itemsById.get(entry.id)!,
        })),
    [entries, isVisible, itemsById],
  );

  const handleSelectionsChange = React.useCallback(
    (next: EntitySelection[]) => {
      const nextIds = new Set(next.map((s) => s.id));
      let nextVisibility = columnVisibility;
      let futureVisible = visibleCount;
      let changed = false;
      for (const entry of entries) {
        const visible = columnVisibility[entry.id] !== false;
        const willBeVisible = nextIds.has(entry.id);
        if (visible === willBeVisible) continue;
        // Same guards as the switch path: unhideable columns stay, and the
        // last visible column cannot go.
        if (!willBeVisible && (!entry.canHide || futureVisible <= 1)) continue;
        nextVisibility = { ...nextVisibility, [entry.id]: willBeVisible };
        futureVisible += willBeVisible ? 1 : -1;
        changed = true;
      }
      if (changed) onColumnVisibilityChange(nextVisibility);
    },
    [entries, columnVisibility, visibleCount, onColumnVisibilityChange],
  );

  return (
    <EntitySelector
      config={config}
      value={selections}
      onValueChange={handleSelectionsChange}
    >
      <EntitySelectorTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            className="rounded-full"
            aria-label={label}
          >
            <Icon as={Settings} size="md" />
          </Button>
        }
      />
      <EntitySelectorContent align="end" size="sm" maxHeight={520}>
        <EntitySelectorInput placeholder="Search columns..." />
        <ShownColumns
          label={label}
          pinnedEntries={pinnedEntries}
          shownEntries={shownEntries}
          columnVisibility={columnVisibility}
          isToggleDisabled={isToggleDisabled}
          onToggleVisibility={toggleVisibility}
          onMove={moveShown}
          useReorderButtons={useReorderButtons}
        />
        <EntitySelectorPool value={POOL_ID} hideSelectionHighlight />
        {onReset ? (
          <EntitySelectorFooter>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={onReset}
            >
              <Text size="sm" variant="muted">
                Reset columns
              </Text>
            </Button>
          </EntitySelectorFooter>
        ) : null}
      </EntitySelectorContent>
    </EntitySelector>
  );
}
